import type {
  ButtonName,
  EOIButtonSource,
  EoiInteraction,
  EoiSection,
  EoiStatus,
  LinkType,
} from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { appendUtmParametersToApplicationLink } from 'bundles/expression-of-interest/utils/util';

type interactEOIEventProps = {
  eoiInteraction: EoiInteraction;
  eoiSection?: EoiSection | null;
  trackingIndex?: number;
  personalizedMessageName?: string;
  eoiButtonSource?: EOIButtonSource;
};

type submitEOIEventProps = {
  eoiStatus: EoiStatus;
  eoiSection: EoiSection;
  trackingIndex?: number;
  eoiButtonSource?: EOIButtonSource;
};

/**
 * This hook provides functions to track different types of EOI-related interactions, submissions, and button clicks.
 *
 * @param {string} productId - The ID of the product for tracking purposes.
 * @returns {Object} An object containing the tracking functions.
 * @returns {Function} trackInteractEoi - Function to track EOI interactions.
 * @returns {Function} trackSubmitEoi - Function to track EOI submissions.
 * @returns {Function} trackClickButton - Function to track button clicks.
 */
export const useEoiEventing = (productId: string) => {
  const track = useTracker();

  const trackInteractEoi = ({
    eoiInteraction,
    eoiSection,
    trackingIndex,
    personalizedMessageName,
    eoiButtonSource,
  }: interactEOIEventProps) => {
    track('interact_eoi', {
      eoiInteraction,
      ...(eoiSection && { eoiSection }),
      pageSection: {
        sectionName: 'eoi',
        subindex: trackingIndex ?? 1,
      },
      ...(personalizedMessageName && {
        personalizedMessage: {
          messageId: personalizedMessageName,
        },
      }),
      product: {
        id: productId,
      },
      ...(eoiButtonSource && { eoiButtonSource }),
    });
  };

  const trackSubmitEoi = ({ eoiStatus, eoiSection, trackingIndex, eoiButtonSource }: submitEOIEventProps) => {
    track('submit_eoi', {
      eoiStatus,
      eoiSection,
      pageSection: {
        sectionName: 'eoi',
        subindex: trackingIndex ?? 1,
      },
      product: {
        id: productId,
      },
      ...(eoiButtonSource && { eoiButtonSource }),
    });
  };

  const trackClickButton = (buttonName: ButtonName, linkType?: LinkType, link?: string, trackingIndex?: number) => {
    const linkURL = link ? appendUtmParametersToApplicationLink(link) : null;
    track('click_button', {
      button: {
        name: buttonName,
        ...(linkType && {
          linkType,
        }),
        ...(linkURL && {
          linkURL,
        }),
      },
      pageSection: {
        sectionName: 'eoi',
        subindex: trackingIndex ?? 1,
      },
      product: {
        id: productId,
      },
    });
  };

  return {
    trackInteractEoi,
    trackSubmitEoi,
    trackClickButton,
  };
};
